import { Checkbox, Drawer, FormControl, FormControlLabel, Grid, Icon, InputLabel, MenuItem, Select } from "@mui/material";
import { useEffect, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { RoleEnum } from "../../_common/enum";
import { PrimaryButton } from "../../_components/button/primary.button";
import { DrawerHeader } from "../../_components/drawer/header.drawer";
import { TextValidatorHcs } from "../../_components/textValidator/textValidatorHcs";
import { toastr } from "../../_components/toastr/toastr";
import { RESET_PASSWORD_ERROR_MESSAGES, RESET_PASSWORD_HELPER_TEXT, RESET_PASSWORD_VALIDATORS } from "../../_const/security.const";
import { handleErrorResponse, handleResponse } from "../../_helpers/handleResponse.helper";
import { accountService } from "../../_services/accounts.service";
import { usersService } from "../../_services/users.service";
import { IAccountManage } from "../../_common/interface/accounts/iAccountManage";
import { useDrawerStyles } from "../../_styles/drawer.styles";
import { UserStatusEnum } from "../../_common/enum/userStatus.enum";

export const AccountManageDrawer = (params: { isOpen: boolean, itemId: string | null, onSave(): void, onClose(): void }) => {

    const resetData = () => ({
        id: "",
        name: "",
        email: "",
        role: "",
        roles: [],
        userStatusId: 0,
        password: "",
        confirmPassword: "",
        isActive: true
    });
    const [passwordChange, setPasswordChange] = useState(false);
    const [data, setData] = useState<IAccountManage>(resetData);

    useEffect(() => {
        if (params.itemId != null)
            fetchData();
        else
            setData(resetData);
    }, [params.itemId]);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);


    function fetchData() {
        if (!params.itemId) return;

        setIsLoading(true)
        return usersService
            .get(params.itemId)
            .then(
                (json) => {
                    setData(_data => { _data.role = json.roles; return { ..._data, ...json } });
                }, handleErrorResponse
            )
            .finally(() => {
                setIsLoading(false);
            });
    }

    function submit(e: any) {
        e.preventDefault();
        saveUser();
    }

    function saveUser() {
        setIsSaving(true);

        var _data = { ...data, isActive: true };
        _data.roles = _data.id != null ? [] : _data.roles || [];
        _data.roles.push(_data.role || "");
        console.log(_data);

        accountService
            .submit(_data)
            .then(
                (d) => {
                    toastr.success("Saved successfully");
                    resetData();
                    params.onSave();
                },
                (e) => {
                    toastr.error("Something went wrong. Please try again later")
                }
            ).finally(() => setIsSaving(false));
    }

    function disable() {
        setIsLoading(true);
        return accountService
            .disable(params.itemId)
            .then(
                (d) => {
                    toastr.success("Disabled successfully");
                    fetchData();
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }

    function handleChange(event: any) {
        setData((__data) => { return { ...__data, [event.target.name]: event.target.value } });
    };


    return (
        <Drawer
            anchor={"right"}
            variant="persistent"
            open={params.isOpen}
            ModalProps={{
                keepMounted: true
            }}
        >

            <div
                tabIndex={0}
                role="button">

                <div>

                    <DrawerHeader name={(!!params.itemId) ? "Update User" : "Create User"} onClose={params.onClose} />

                    <ValidatorForm
                        onSubmit={submit}
                        noValidate>

                        <Grid container spacing={1} sx={useDrawerStyles.container}>

                            <Grid item xs={12}>
                                <TextValidatorHcs
                                    key="name"
                                    name="name"
                                    label="Name *"
                                    fullWidth
                                    validators={['required', 'minStringLength:1']}
                                    errorMessages={['required field', "Field cannot be empty"]}
                                    value={data.name}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextValidatorHcs
                                    key="email"
                                    name="email"
                                    label="Email *"
                                    fullWidth
                                    validators={['required', 'minStringLength:1']}
                                    errorMessages={['required field', "Field cannot be empty"]}
                                    value={data.email}
                                    onChange={handleChange}
                                />
                            </Grid>

                            {(data.id === "") &&
                                <Grid item xs={12}>
                                    <TextValidatorHcs
                                        autoComplete="off"
                                        required
                                        fullWidth
                                        label="Initial Password"
                                        name="password"
                                        value={data.password}
                                        disabled={isLoading}
                                        onChange={handleChange}
                                        type="password"
                                        validators={RESET_PASSWORD_VALIDATORS(true)}
                                        errorMessages={RESET_PASSWORD_ERROR_MESSAGES(true)}
                                        helperText={!data.password ? RESET_PASSWORD_HELPER_TEXT : "Password is valid"}
                                    />
                                </Grid>
                            }

                            {(data.id !== "") &&
                                <Grid item xs={12}>
                                    <FormControlLabel control={<Checkbox checked={passwordChange} onChange={() => setPasswordChange(!passwordChange)} />} label="Change Password" />

                                    {passwordChange === true &&
                                        <TextValidatorHcs
                                            autoComplete="off"
                                            fullWidth
                                            label="Password"
                                            name="password"
                                            value={data?.password}
                                            onChange={handleChange}
                                            type="password"
                                            validators={RESET_PASSWORD_VALIDATORS(true)}
                                            errorMessages={RESET_PASSWORD_ERROR_MESSAGES(true)}
                                            helperText={!data?.password ? "Only Update if password change is required. " + RESET_PASSWORD_HELPER_TEXT : "✓ Password is valid"} />
                                    }
                                </Grid>
                            }

                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>User Role</InputLabel>
                                    <Select
                                        label="User Role"
                                        name="role"
                                        value={data.role}
                                        placeholder="User Role"
                                        onChange={handleChange}
                                        variant="outlined">
                                        <MenuItem value={RoleEnum.Admin}>Admin</MenuItem>
                                        <MenuItem value={RoleEnum.User}>User</MenuItem>
                                        <MenuItem value={RoleEnum.Organiser}>Organiser</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {data.id !== "" &&
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>User Status</InputLabel>
                                        <Select
                                            label="User Status"
                                            name="userStatusId"
                                            value={data.userStatusId}
                                            placeholder="User Status"
                                            onChange={handleChange}
                                            variant="outlined">
                                            <MenuItem value={UserStatusEnum.Active}>Active</MenuItem>
                                            <MenuItem value={UserStatusEnum.Suspended}>Suspended</MenuItem>
                                            <MenuItem value={UserStatusEnum.Deceased}>Deceased</MenuItem>
                                            <MenuItem value={UserStatusEnum.Removed}>Removed</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            }

                        </Grid>

                        <Grid container spacing={1} sx={useDrawerStyles.container}>

                            <Grid item xs={(data.id === "" ? 12 : data?.isActive === false ? 12 : 6)}>
                                <PrimaryButton
                                    isSaving={isSaving}
                                    isLoading={isLoading}
                                    type="submit"
                                    variant="save">{(data?.isActive === false) ? "Save and Reactivate" : "Save"}
                                </PrimaryButton>
                            </Grid>

                            {(data.id.length > 0 && data?.isActive === true) &&
                                <Grid item xs={6}>
                                    <PrimaryButton
                                        type="button"
                                        variant="danger"
                                        disabled={isLoading}
                                        onClick={disable}
                                        icon="person_off"
                                    >
                                        Disable
                                    </PrimaryButton>
                                </Grid>
                            }

                        </Grid>

                    </ValidatorForm>
                </div>
            </div>
        </Drawer>
    );
};